import React from 'react'
import { createPortal } from 'react-dom'
import { XMarkIcon } from '@heroicons/react/24/outline'

export type AlxModalWidth = 'md' | 'lg' | 'xl'

const WIDTH_CLASS: Record<AlxModalWidth, string> = {
  md: 'max-w-2xl',
  lg: 'max-w-4xl',
  xl: 'max-w-5xl'
}

export interface AlxModalShellProps {
  open: boolean
  onClose: () => void
  title: string
  subtitle?: string
  icon?: React.ReactNode
  width?: AlxModalWidth
  zIndex?: number
  testId?: string
  titleId?: string
  closeDisabled?: boolean
  footer?: React.ReactNode
  children: React.ReactNode
}

export default function AlxModalShell({
  open,
  onClose,
  title,
  subtitle,
  icon,
  width = 'md',
  zIndex = 10010,
  testId,
  titleId = 'alx-modal-title',
  closeDisabled = false,
  footer,
  children
}: AlxModalShellProps) {
  if (!open) return null

  const content = (
    <div
      className="fixed inset-0 flex items-center justify-center bg-blueprint-blue/40 backdrop-blur-sm px-4 py-6"
      style={{ zIndex }}
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      data-testid={testId}
    >
      <div
        className={`relative flex max-h-[90vh] w-full ${WIDTH_CLASS[width]} flex-col overflow-hidden rounded-2xl border border-[#E2E5EA] bg-white shadow-large`}
      >
        <div className="flex items-center gap-3 border-b border-[#E2E5EA] bg-page-bg px-6 py-4">
          {icon ? (
            <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-white text-blueprint-blue ring-1 ring-inset ring-[#E2E5EA]">
              {icon}
            </div>
          ) : null}
          <div className="min-w-0 flex-1">
            <h2 id={titleId} className="font-heading text-lg font-semibold text-blueprint-blue">
              {title}
            </h2>
            {subtitle ? <p className="text-xs text-heritage-grey">{subtitle}</p> : null}
          </div>
          <button
            type="button"
            className="-mr-2 rounded-full p-2 text-heritage-grey transition-colors hover:bg-white hover:text-blueprint-blue"
            onClick={onClose}
            aria-label="Close"
            disabled={closeDisabled}
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5">{children}</div>

        {footer ? (
          <div className="flex items-center justify-end gap-2 border-t border-[#E2E5EA] bg-page-bg px-6 py-4">
            {footer}
          </div>
        ) : null}
      </div>
    </div>
  )

  return createPortal(content, document.body)
}
